import { useCallback, useEffect, useState } from 'react'
import type { MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Group from 'components/utils/Group'
import config from 'constants/config'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { consentMessages } from '../../../lib/consent/messages'
import { OPEN_CONSENT_SETTINGS_EVENT } from '../../consent/openSettings'
import { reloadPage } from '../../consent/reloadPage'
import { clearUngrantedPurposeIdentifiers } from '../../consent/clearWithdrawnIdentifiers'
import { continueWithoutOptionalCookies, readConsentState, writeConsentState } from '../../consent/deviceState'
import { CONSENT_PURPOSES } from '../../consent/types'
import type { ConsentPurpose } from '../../consent/types'
import ConsentPurposeRow from './ConsentPurposeRow'

type ConsentView = 'first-layer' | 'configure' | 'confirmation'

const APPLY_RELOAD_DELAY_MS = 250

const OPTIONAL_PURPOSE_COPY = [
  {
    purpose: 'analytics' as const,
    title: consentMessages.analytics,
    description: consentMessages.analyticsDescription,
  },
  {
    purpose: 'personalisation' as const,
    title: consentMessages.personalisation,
    description: consentMessages.personalisationDescription,
  },
  {
    purpose: 'experimentation' as const,
    title: consentMessages.experimentation,
    description: consentMessages.experimentationDescription,
  },
  {
    purpose: 'advertising' as const,
    title: consentMessages.advertising,
    description: consentMessages.advertisingDescription,
  },
]

function ConsentBanner() {
  const intl = useIntl()
  const currentConsent = readConsentState()
  const [isOpen, setIsOpen] = useState(!currentConsent)
  const [view, setView] = useState<ConsentView>(currentConsent ? 'configure' : 'first-layer')
  const [openedFromSettings, setOpenedFromSettings] = useState(!!currentConsent)
  const [purposes, setPurposes] = useState<Array<ConsentPurpose>>(currentConsent?.purposes ?? [])
  const [isApplying, setIsApplying] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)

  useEffect(() => {
    const openSettings = () => {
      const current = readConsentState()
      setPurposes(current?.purposes ?? [])
      setOpenedFromSettings(true)
      setView('configure')
      setIsApplying(false)
      setSaveFailed(false)
      setIsOpen(true)
    }
    window.addEventListener(OPEN_CONSENT_SETTINGS_EVENT, openSettings)
    return () => window.removeEventListener(OPEN_CONSENT_SETTINGS_EVENT, openSettings)
  }, [])

  useEffect(() => {
    if (!isApplying) return
    const timeout = window.setTimeout(() => {
      try {
        clearUngrantedPurposeIdentifiers(readConsentState()?.purposes ?? [])
      } finally {
        // Stopping existing integrations must not depend on storage availability.
        reloadPage()
      }
    }, APPLY_RELOAD_DELAY_MS)
    return () => window.clearTimeout(timeout)
  }, [isApplying])

  const privacyURL = config.contentLinks.privacyPolicy.href || config.contentLinks.privacyPolicy.to!

  const save = useCallback((nextPurposes: Array<ConsentPurpose>) => {
    const saved = writeConsentState(nextPurposes.length ? 'accepted' : 'necessary_only', nextPurposes)
    setSaveFailed(!saved)
    return !!saved
  }, [])

  const applyAndReload = useCallback((nextPurposes?: Array<ConsentPurpose>) => {
    if (isApplying) return
    if (nextPurposes && !save(nextPurposes)) return
    setIsApplying(true)
  }, [isApplying, save])

  const continueWithoutSaving = useCallback(() => {
    continueWithoutOptionalCookies()
    clearUngrantedPurposeIdentifiers([])
    setIsOpen(false)
  }, [])

  const acceptAll = useCallback(() => {
    applyAndReload([...CONSENT_PURPOSES])
  }, [applyAndReload])

  const acceptNecessary = useCallback(() => {
    applyAndReload([])
  }, [applyAndReload])

  const configure = useCallback(() => {
    if (isApplying) return
    setPurposes([])
    setOpenedFromSettings(false)
    setView('configure')
  }, [isApplying])

  const saveConfiguration = useCallback(() => {
    if (isApplying) return
    if (!save(purposes)) return
    setView('confirmation')
    setIsApplying(true)
  }, [isApplying, purposes, save])

  const applySavedChoices = useCallback(() => {
    applyAndReload()
  }, [applyAndReload])

  const discardConfigure = useCallback(() => {
    if (isApplying) return
    if (openedFromSettings) {
      setIsOpen(false)
      return
    }
    setPurposes([])
    setView('first-layer')
  }, [isApplying, openedFromSettings])

  const onTogglePurpose = useCallback((purpose: ConsentPurpose, checked: boolean) => {
    setPurposes(current => {
      if (checked) {
        return current.includes(purpose) ? current : [...current, purpose]
      }
      return current.filter(item => item !== purpose)
    })
  }, [])

  const onModalClose = useCallback((event: MouseEvent<HTMLElement>) => {
    event.preventDefault()
    if (isApplying) return
    if (view === 'confirmation') {
      applySavedChoices()
      return
    }
    if (view === 'configure') {
      discardConfigure()
    }
  }, [applySavedChoices, discardConfigure, isApplying, view])

  const heading = view === 'first-layer'
    ? intl.formatMessage(consentMessages.heading)
    : intl.formatMessage(consentMessages.cookieSettings)
  const isFirstLayer = view === 'first-layer'
  const cookiePolicyLabel = intl.formatMessage(consentMessages.cookiePolicy)
  const applyingLabel = intl.formatMessage(consentMessages.applyingChoices)

  if (!isOpen) return undefined

  return (
    <ModalBase
      size="S"
      isOpen
      dismissible={!isFirstLayer && !isApplying}
      onClose={onModalClose}
      data-testid="consent-modal"
    >
      <LoadingIndicator
        visible={isApplying}
        floating
        opaque
        aria-live="polite"
        aria-label={applyingLabel}
      >
        <BodyText variant="medium" align="center">{applyingLabel}</BodyText>
      </LoadingIndicator>
      <ModalHeader
        title={heading}
        dismissible={!isFirstLayer && !isApplying}
        onBackButtonClick={view === 'configure' && !isApplying ? discardConfigure : undefined}
        onCloseButtonClick={isFirstLayer || isApplying ? undefined : onModalClose}
      />
      <ModalBody>
        <ModalContent>
          {saveFailed && <BodyText variant="medium" role="alert">{intl.formatMessage(consentMessages.saveError)}</BodyText>}
          {isFirstLayer && (
            <Group direction="vertical" gap={16}>
              <BodyText variant="large">{intl.formatMessage(consentMessages.responsibleParty)}</BodyText>
              <BodyText variant="large">{intl.formatMessage(consentMessages.optionalUse)}</BodyText>
              <BodyText variant="large">{intl.formatMessage(consentMessages.changeOrWithdraw)}</BodyText>
              <TextLink
                href={privacyURL}
                target="_blank"
                rel="noopener noreferrer"
                weight="regular"
              >
                {cookiePolicyLabel}
              </TextLink>
            </Group>
          )}
          {view === 'configure' && (
            <Group direction="vertical" gap={24}>
              <ConsentPurposeRow
                title={intl.formatMessage(consentMessages.necessaryTitle)}
                description={intl.formatMessage(consentMessages.necessaryDescription)}
                checked
                disabled
              />
              {OPTIONAL_PURPOSE_COPY.map(item => (
                <ConsentPurposeRow
                  key={item.purpose}
                  name={item.purpose}
                  title={intl.formatMessage(item.title)}
                  description={intl.formatMessage(item.description)}
                  checked={purposes.includes(item.purpose)}
                  onToggle={onTogglePurpose}
                />
              ))}
            </Group>
          )}
          {view === 'confirmation' && (
            <BodyText variant="large">{intl.formatMessage(consentMessages.savedConfirmation)}</BodyText>
          )}
        </ModalContent>
      </ModalBody>
      <ModalFooter actionsLayout="stacked" tabletActionsLayout="stacked">
        {saveFailed && !currentConsent && (
          <TextButton kind="secondary" fit="full-width" size="large" onClick={continueWithoutSaving}>
            {intl.formatMessage(consentMessages.continueNecessary)}
          </TextButton>
        )}
        {isFirstLayer && (
          <>
            <TextButton kind="secondary" fit="full-width" size="large" disabled={isApplying} onClick={acceptAll}>
              {intl.formatMessage(consentMessages.acceptAll)}
            </TextButton>
            <TextButton kind="secondary" fit="full-width" size="large" disabled={isApplying} onClick={acceptNecessary}>
              {intl.formatMessage(consentMessages.necessaryOnly)}
            </TextButton>
            <TextButton kind="secondary" fit="full-width" size="large" disabled={isApplying} onClick={configure}>
              {intl.formatMessage(consentMessages.configure)}
            </TextButton>
          </>
        )}
        {view === 'configure' && (
          <TextButton kind="primary" fit="full-width" size="large" disabled={isApplying} onClick={saveConfiguration}>
            {intl.formatMessage(consentMessages.saveChoices)}
          </TextButton>
        )}
        {view === 'confirmation' && (
          <TextButton kind="primary" fit="full-width" size="large" disabled={isApplying} onClick={applySavedChoices}>
            {intl.formatMessage(consentMessages.gotIt)}
          </TextButton>
        )}
      </ModalFooter>
    </ModalBase>
  )
}

export default ConsentBanner
